import { useEffect, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { printJobAreas } from "../lib/qz";
import { bgInterval, keepTabAwake } from "../lib/bgTimer";

const DONE_KEY = "pq_done";
const deviceId = () => localStorage.getItem("pos_device_id") || "";
const readDone = () => { try { return JSON.parse(localStorage.getItem(DONE_KEY) || "{}"); } catch { return {}; } };
const writeDone = (m) => localStorage.setItem(DONE_KEY, JSON.stringify(m));
const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

async function processJob(job, areas, settings, qzDown) {
  const done = readDone();
  const already = [...new Set([...(job.areas_done || []), ...(done[job.id] || [])])];
  const markArea = (aid) => { const m = readDone(); m[job.id] = [...new Set([...(m[job.id] || []), aid])]; writeDone(m); };
  const order = { ...job.order, items: job.items, copy: job.copy, copy_by: job.copy_by, copy_at: job.copy_at };
  let errors = [];
  let qzFailed = false;
  if (qzDown) {
    errors = ["QZ Tray no disponible en la estación de impresión"];
  } else {
    try {
      ({ errors } = await withTimeout(printJobAreas(order, areas, settings.ticket_size || "80", already, markArea), 120000));
    } catch (e) {
      errors = ["QZ Tray no disponible en la estación de impresión"];
      qzFailed = true;
    }
  }
  const doneAreas = readDone()[job.id] || [];
  await api.post(`/print/jobs/${job.id}/result`, { done_areas: doneAreas, ok: errors.length === 0, error: errors.join(" · ") }, { params: { device_id: deviceId() } });
  const m = readDone(); delete m[job.id]; writeDone(m);
  return qzFailed;
}

// Solo el equipo designado como estación de impresión imprime las comandas remotas (QR, web, comandero).
export function PrintStationWorker() {
  const { user } = useAuth();
  const busy = useRef(false);
  const ctx = useRef({ areas: [], settings: {}, at: 0 });
  useEffect(() => {
    if (!user || user.role === "superadmin" || user.license_expired) return undefined;
    let alive = true;
    let nextAt = 0;
    let lastBeat = 0;
    const loadCtx = async () => {
      if (Date.now() - ctx.current.at < 60000) return;
      const [a, s] = await Promise.all([api.get("/comanda-areas"), api.get("/settings")]);
      ctx.current = { areas: a.data || [], settings: s.data || {}, at: Date.now() };
    };
    const beat = () => {
      if (!window.__isPrintStation || !deviceId() || Date.now() - lastBeat < 15000) return;
      lastBeat = Date.now();
      api.post("/print/heartbeat", null, { params: { device_id: deviceId() } }).catch(() => { lastBeat = 0; });
    };
    const tick = async () => {
      if (!alive || busy.current || !deviceId() || Date.now() < nextAt) return;
      busy.current = true;
      let next = 30000;
      try {
        const { data } = await api.get("/print/queue", { params: { device_id: deviceId() } });
        window.__isPrintStation = !!data.is_station;
        if (data.is_station) {
          lastBeat = Date.now();
          keepTabAwake("pos-print-station");
          next = 5000;
          if (data.jobs.length) await loadCtx();
          let qzDown = false;
          for (const job of data.jobs) {
            if (!alive) break;
            try { qzDown = (await processJob(job, ctx.current.areas, ctx.current.settings, qzDown)) || qzDown; } catch (e) { break; }
          }
        }
      } catch (e) { next = 10000; } finally { busy.current = false; nextAt = Date.now() + next; }
    };
    const stop = bgInterval(() => { beat(); tick(); }, 5000);
    const kick = () => { nextAt = 0; lastBeat = 0; tick(); };
    const onVisible = () => { if (document.visibilityState === "visible") kick(); };
    const onSettings = () => { ctx.current.at = 0; };
    window.addEventListener("settings-updated", onSettings);
    window.addEventListener("print-station-changed", kick);
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", onVisible);
    tick();
    return () => {
      alive = false; stop();
      window.removeEventListener("settings-updated", onSettings);
      window.removeEventListener("print-station-changed", kick);
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);
  return null;
}
